import { TypographyProps } from "@tm/components"
import { ILocalizationContext } from "@tm/localization"
import {
    ArticleAttribute,
    ArticleAttributeParams,
    ArticleAttributesParams,
    ArticleReference,
    ArticleReferenceParms,
    AttributeAction,
    AttributeComparisonModel,
    CompareWithVehicleRecordsAttributeVisualization,
    EMediaType,
    ErpPrice,
    ListFilter,
    ModificationState,
    OE,
    PriceType,
} from "@tm/models"
import { concat } from "@tm/utils"
import { ArticleAttributeFilter } from "../../../../data/model"
import { MultipleValueAttribute } from "../../../_shared/article-item/attribute-group"

export function mapPriceVariant(type: PriceType): TypographyProps["variant"] {
    switch (type) {
        case PriceType.Strikethrough:
            return "label"
        default:
            return "body2"
    }
}

export function mapPriceColor(type: PriceType): TypographyProps["color"] {
    switch (type) {
        case PriceType.Strikethrough:
            return "primary"
        case PriceType.Purchase:
            return "primary"
        default:
            return ""
    }
}

export function mapPriceFontWeight(type: PriceType): TypographyProps["fontWeight"] {
    switch (type) {
        case PriceType.Purchase:
            return "bold"
        default:
            return 600
    }
}

export function mapArticleAttributeFilter(attribute: ArticleAttribute): ArticleAttributeFilter {
    const query = `${attribute.id}|${attribute.id || attribute}`
    return {
        id: attribute.id,
        group: "",
        description: attribute.description,
        isSelected: true,
        name: attribute.abbreviation,
        query,
        unit: attribute.unit,
        priority: 0,
        text: attribute.text || attribute.value,
    }
}

export function mapArticleAttributeParms(attribute: MultipleValueAttribute): ArticleAttributeParams {
    return {
        id: attribute.id,
        key: attribute.key,
        label: attribute.abbreviation,
        values: attribute.values.map((value) => ({
            id: value.id,
            isSelected: false,
            value: value.value,
            displayValue: `${value.value} ${value.unit || ""}`.trim(),
            modificationState: value.modificationState ?? ModificationState.NotModified,
            isHighlighted: value.highlight,
            tooltip: value.tooltip,
            isOptimized: value.isOptimized,
            alternatives:
                value.alternatives?.map((alternative) => `${alternative.abbreviation}: ${`${alternative.value} ${alternative.unit || ""}`.trim()}`) ??
                [],
        })),
    }
}

export function mapAttributes(
    translateText: ILocalizationContext["translateText"],
    attributes: Array<ArticleAttribute>,
    productGroupId?: number,
    vehicleRecordAttributes?: AttributeComparisonModel[],
    selectedAttributes?: ListFilter[],
    topAttributes?: Array<ArticleAttributeParams>
): Array<ArticleAttributeParams> {
    const mapped: Array<ArticleAttributeParams> = []

    attributes.forEach((attribute) => {
        let found = mapped.find(
            (attr) =>
                attr.id === attribute.id && // check if the attribute is already existing
                !!attribute.abbreviation // don't combine attributes without an abbrevation/label
        )

        // make sure the article is not already mentioned in the topArticleAttributes
        if (!found && !!topAttributes?.length) {
            found = topAttributes.find(
                (attr) =>
                    attr.id === attribute.id && // check if the attribute is already existing
                    !!attribute.abbreviation // don't combine attributes without an abbrevation/label
            )
        }

        if (!found) {
            found = {
                id: attribute.id,
                key: attribute.key,
                label: attribute.abbreviation,
                values: [],
                isSharedAttribute: attribute.isSharedAttribute,
                isArticleAttribute: attribute.isArticleAttribute,
            }
            mapped.push(found)
        }

        const isHighlighted =
            attribute.highlight ||
            vehicleRecordAttributes?.some(
                (x) =>
                    attribute.id === x.attribute.id &&
                    attribute.key === x.attribute.value &&
                    productGroupId === x.attribute.productGroupId &&
                    (x.visualization === CompareWithVehicleRecordsAttributeVisualization.Suggested ||
                        x.visualization === CompareWithVehicleRecordsAttributeVisualization.Confirmed)
            )

        const foundDuplicatedValue = found.values?.some((value) => value.value === attribute.value)

        if (!foundDuplicatedValue) {
            const alternatives =
                attribute.alternatives?.map(
                    (alternative) => `${alternative.abbreviation}: ${`${alternative.value} ${alternative.unit || ""}`.trim()}`
                ) ?? []

            const alternativesTooltips = alternatives.length
                ? `${translateText(attribute.isOptimized ? 1642 : 1643)}:\n${alternatives.join("\n")}`
                : undefined
            const tooltip = concat(`\n`, alternativesTooltips, attribute.tooltip)

            const matchedSelectedAttribute = selectedAttributes?.find((x: ListFilter) => x.id === attribute.id && x.value === attribute.value)

            found.values = [
                ...(found.values ?? []),
                {
                    id: attribute.id,
                    isSelected: !!matchedSelectedAttribute,
                    isSelectable: attribute.isSelectable || (attribute.action ?? AttributeAction.Filter) > 0,
                    value: attribute.value,
                    displayValue: `${attribute.value} ${attribute.unit || ""}`.trim(),
                    modificationState: attribute.modificationState ?? ModificationState.NotModified,
                    isHighlighted,
                    tooltip,
                    isOptimized: attribute.isOptimized,
                    alternatives,
                    action: attribute.action,
                },
            ]
        }
    })

    return mapped
}

export function mapArticleReferences(reference: ArticleReference): ArticleReferenceParms {
    return {
        label: reference.referenceNo,
        referenceType: reference.referenceType,
        modificationState: reference.modificationState,
        description: reference.description,
        manufacturer: reference.manufacturer,
    }
}

export function getIconFromMediaType(mediaType: EMediaType) {
    switch (mediaType) {
        case EMediaType.Video:
        case EMediaType.Audio:
            return "arrow-right"
        case EMediaType.Pdf:
            return "file-pdf"
        case EMediaType.Website:
        case EMediaType.YoutubeChannel:
            return "external-link"
        default:
    }
}

export function mapArticleAttributes(
    translateText: ILocalizationContext["translateText"],
    part: OE.OePart,
    displayVehicleOeCriteria?: boolean
): ArticleAttributesParams[] {
    return (
        part?.attributes
            ?.map((attributes) => {
                const additionalAttributes = [...attributes.partsListAttributes, ...attributes.articleAttributes]
                if (displayVehicleOeCriteria) {
                    additionalAttributes.push(...attributes.vehicleAttributes)
                }

                const result: ArticleAttributesParams = {
                    topArticleAttributes: mapAttributes(translateText, [...attributes.topAttributes]),
                    additionalAttributes: mapAttributes(translateText, additionalAttributes),
                }
                return result
            })
            ?.filter((x) => x.topArticleAttributes?.length || x.additionalAttributes?.length) ?? []
    )
}

export function mapOePrices(translateText: ILocalizationContext["translateText"], part: OE.OePart, showOePrice?: boolean) {
    const oePrice: ErpPrice[] = !showOePrice
        ? []
        : part.prices?.map((price) => {
              // Make sure we use an ErpPrice here, so we can use the same component to show this price
              return {
                  type: PriceType.None,
                  description: price.description || translateText(1620),
                  shortDescription: price.description || translateText(1620),
                  value: price.value,
                  currencySymbol: "€",
                  currencyCode_Iso_4217: price.currency,
                  vat: NaN,
              }
          }) || []

    return oePrice
}
